import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Send, MessageSquare, BarChart3, Mail, MessageCircle, User } from "lucide-react";
import { Modal, TextInput, SelectInput, PrimaryBtn, GhostBtn, EmptyState, StatCard } from "./CrmUI";
import {
    getTemplates, createTemplate, updateTemplate, deleteTemplate,
    getCrmConfig, broadcast, getBroadcasts,
    getClients, sendEmailOne, sendWhatsappText, waLink,
} from "./crmApi";

// ── Send a single message / email to one client ─────────────────────────────
const SingleSendModal = ({ onClose, templates }) => {
    const [clients, setClients] = useState([]);
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(null);
    const [channel, setChannel] = useState("email");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);

    useEffect(() => {
        let active = true;
        (async () => { try { const c = await getClients(); if (active) setClients(c); } catch { /* ignore */ } })();
        return () => { active = false; };
    }, []);

    const matches = query.trim()
        ? clients.filter((c) => [c.name, c.email, c.phone].join(" ").toLowerCase().includes(query.trim().toLowerCase())).slice(0, 6)
        : [];

    const applyTemplate = (id) => {
        const t = templates.find((x) => x.id === id);
        if (!t) return;
        setChannel(t.channel === "email" ? "email" : "whatsapp");
        if (t.subject) setSubject(t.subject);
        if (t.body) setBody(t.body);
    };

    const send = async () => {
        if (!selected) { toast.error("Pick a client first."); return; }
        if (channel === "email") {
            if (!selected.email) { toast.error("This client has no email."); return; }
            if (!subject.trim()) { toast.error("Subject is required."); return; }
            setSending(true);
            try { await sendEmailOne({ to_email: selected.email, to_name: selected.name, subject, body }); toast.success(`Email sent to ${selected.email}`); onClose(); }
            catch (e) { toast.error(e?.response?.data?.detail || "Could not send (check SMTP in Integrations)."); }
            finally { setSending(false); }
        } else {
            if (!selected.phone) { toast.error("This client has no mobile number."); return; }
            if (!body.trim()) { toast.error("Message body is required."); return; }
            setSending(true);
            try {
                await sendWhatsappText(selected.phone, body);
                toast.success(`WhatsApp message sent to ${selected.name}`);
                onClose();
            } catch (e) {
                // Fall back to click-to-chat when the Cloud API isn't configured.
                window.open(waLink(selected.phone, body), "_blank");
                toast.message("Opened WhatsApp web", { description: "Cloud API not configured — sent via click-to-chat instead." });
                onClose();
            } finally { setSending(false); }
        }
    };

    return (
        <Modal open onClose={onClose} title="Send to a client" subtitle="One-to-one email (Gmail SMTP) or WhatsApp" maxWidth="max-w-xl" testId="single-send-modal">
            <div className="space-y-4">
                {!selected ? (
                    <div>
                        <TextInput label="Find client" value={query} onChange={setQuery} placeholder="Search by name, email or phone…" testId="single-search" />
                        {matches.length > 0 && (
                            <div className="mt-2 rounded-xl border border-oth-line/70 bg-white divide-y divide-oth-line/50 overflow-hidden" data-testid="single-results">
                                {matches.map((c) => (
                                    <button key={c.id} onClick={() => { setSelected(c); setSubject(`A note from One Tree Hill Prime Wealth`); }} className="w-full text-left px-4 py-2.5 hover:bg-[#F5F5F2] flex items-center gap-3" data-testid={`single-pick-${c.id}`}>
                                        <span className="w-8 h-8 rounded-full bg-[#EFEFEA] flex items-center justify-center text-oth-ink2 shrink-0"><User size={15} /></span>
                                        <span className="min-w-0">
                                            <span className="block font-display text-sm font-semibold truncate">{c.name}</span>
                                            <span className="block text-[12px] text-oth-ink3 truncate">{c.email || "no email"} · {c.phone || "no phone"}</span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center justify-between rounded-xl border border-oth-line/70 bg-white px-4 py-3" data-testid="single-selected">
                        <div className="min-w-0">
                            <div className="font-display text-sm font-semibold truncate">{selected.name}</div>
                            <div className="text-[12px] text-oth-ink3 truncate">{selected.email || "no email"} · {selected.phone || "no phone"}</div>
                        </div>
                        <button onClick={() => setSelected(null)} className="text-[12px] font-body text-oth-blue hover:underline" data-testid="single-change">Change</button>
                    </div>
                )}

                <div className="flex items-center gap-2">
                    <button onClick={() => setChannel("email")} className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-body border transition-all ${channel === "email" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-oth-ink2 border-oth-line/70 hover:border-oth-ink"}`} data-testid="single-channel-email"><Mail size={15} /> Email (SMTP)</button>
                    <button onClick={() => setChannel("whatsapp")} className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-body border transition-all ${channel === "whatsapp" ? "bg-[#0B6B3A] text-white border-[#0B6B3A]" : "bg-white text-oth-ink2 border-oth-line/70 hover:border-oth-ink"}`} data-testid="single-channel-whatsapp"><MessageCircle size={15} /> WhatsApp</button>
                </div>

                {templates.length > 0 && (
                    <SelectInput label="Use a saved message (optional)" value="" onChange={applyTemplate} options={[{ value: "", label: "— start from scratch —" }, ...templates.map((t) => ({ value: t.id, label: `${t.name} (${t.channel})` }))]} testId="single-template" />
                )}

                {channel === "email" && <TextInput label="Subject" value={subject} onChange={setSubject} testId="single-subject" />}
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1.5">Message</div>
                    <textarea value={body} onChange={(e) => setBody(e.target.value)} className="w-full bg-white border border-oth-line/70 rounded-xl px-3.5 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue min-h-[110px]" placeholder="Type your message…" data-testid="single-body" />
                </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
                <GhostBtn onClick={onClose} testId="single-cancel">Cancel</GhostBtn>
                <PrimaryBtn onClick={send} disabled={sending} testId="single-send"><Send size={14} /> {sending ? "Sending…" : "Send"}</PrimaryBtn>
            </div>
        </Modal>
    );
};

const TemplateModal = ({ onClose, tpl, onSaved }) => {
    const [form, setForm] = useState(() => tpl ? { ...tpl } : { name: "", channel: "whatsapp", subject: "", body: "", wa_template_name: "", wa_language: "en_US" });
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(tpl?.id);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        if (!form.name?.trim()) { toast.error("Name is required."); return; }
        setSaving(true);
        try {
            if (isEdit) await updateTemplate(tpl.id, form); else await createTemplate(form);
            toast.success(isEdit ? "Template updated." : "Template created.");
            onSaved(); onClose();
        } catch { toast.error("Could not save template."); }
        finally { setSaving(false); }
    };

    return (
        <Modal open onClose={onClose} title={isEdit ? "Edit message" : "New message"} subtitle="Reusable reminder / broadcast content" testId="template-modal">
            <div className="space-y-4">
                <TextInput label="Name *" value={form.name} onChange={(v) => set("name", v)} placeholder="e.g. Document request" testId="tpl-name" />
                <SelectInput label="Channel" value={form.channel} onChange={(v) => set("channel", v)} options={[{ value: "whatsapp", label: "WhatsApp" }, { value: "email", label: "Email" }]} testId="tpl-channel" />
                {form.channel === "email" && <TextInput label="Subject" value={form.subject} onChange={(v) => set("subject", v)} testId="tpl-subject" />}
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1.5">Body</div>
                    <textarea value={form.body} onChange={(e) => set("body", e.target.value)} className="w-full bg-white border border-oth-line/70 rounded-xl px-3.5 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue min-h-[100px]" placeholder="e.g. Hi, here's how to set up your SIP…" data-testid="tpl-body" />
                </div>
                {form.channel === "whatsapp" && (
                    <div className="grid grid-cols-2 gap-3 p-3 rounded-xl bg-[#F5F5F2] border border-oth-line/60">
                        <TextInput label="WhatsApp template name" value={form.wa_template_name} onChange={(v) => set("wa_template_name", v)} hint="Approved template in Meta" testId="tpl-wa-name" />
                        <TextInput label="Language code" value={form.wa_language} onChange={(v) => set("wa_language", v)} placeholder="en_US" testId="tpl-wa-lang" />
                    </div>
                )}
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
                <GhostBtn onClick={onClose} testId="tpl-cancel">Cancel</GhostBtn>
                <PrimaryBtn onClick={save} disabled={saving} testId="tpl-save">{saving ? "Saving…" : "Save"}</PrimaryBtn>
            </div>
        </Modal>
    );
};

const BroadcastModal = ({ open, onClose, config, templates, onSent }) => {
    const [form, setForm] = useState({ name: "", template_name: "", language: "en_US", category: "all", audience: "clients", body_params: "" });
    const [sending, setSending] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const send = async () => {
        if (!form.name.trim() || !form.template_name.trim()) { toast.error("Broadcast name and WhatsApp template name are required."); return; }
        setSending(true);
        try {
            const r = await broadcast({
                name: form.name, template_name: form.template_name, language: form.language,
                category: form.category, audience: form.audience,
                body_params: form.body_params ? form.body_params.split(",").map((s) => s.trim()).filter(Boolean) : [],
            });
            toast.success(`Broadcast sent to ${r.sent}/${r.total} recipients.`);
            onSent(); onClose();
        } catch (e) { toast.error(e?.response?.data?.detail || "Broadcast failed. Check WhatsApp config."); }
        finally { setSending(false); }
    };

    const waTemplates = (templates || []).filter((t) => t.channel === "whatsapp" && t.wa_template_name);

    return (
        <Modal open={open} onClose={onClose} title="Send WhatsApp broadcast" subtitle="Uses an approved Meta template" testId="broadcast-modal">
            <div className="space-y-4">
                <TextInput label="Broadcast name *" value={form.name} onChange={(v) => set("name", v)} placeholder="e.g. June SIP reminder" testId="bc-name" />
                {waTemplates.length > 0 && (
                    <SelectInput label="Use saved template" value="" onChange={(v) => { const t = waTemplates.find((x) => x.id === v); if (t) setForm((f) => ({ ...f, template_name: t.wa_template_name, language: t.wa_language || "en_US" })); }}
                        options={[{ value: "", label: "— pick a saved WhatsApp template —" }, ...waTemplates.map((t) => ({ value: t.id, label: t.name }))]} testId="bc-saved-tpl" />
                )}
                <div className="grid grid-cols-2 gap-3">
                    <TextInput label="Meta template name *" value={form.template_name} onChange={(v) => set("template_name", v)} testId="bc-template" />
                    <TextInput label="Language code" value={form.language} onChange={(v) => set("language", v)} testId="bc-lang" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <SelectInput label="Audience" value={form.audience} onChange={(v) => set("audience", v)} options={[{ value: "clients", label: "Clients" }, { value: "leads", label: "Leads" }]} testId="bc-audience" />
                    <SelectInput label="Category segment" value={form.category} onChange={(v) => set("category", v)} options={[{ value: "all", label: "All" }, ...((config?.client_categories || []).map((c) => ({ value: c, label: c })))]} testId="bc-category" />
                </div>
                <TextInput label="Template body variables (optional)" value={form.body_params} onChange={(v) => set("body_params", v)} hint="Comma-separated values for {{1}}, {{2}}…" testId="bc-params" />
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
                <GhostBtn onClick={onClose} testId="bc-cancel">Cancel</GhostBtn>
                <PrimaryBtn onClick={send} disabled={sending} testId="bc-send"><Send size={14} /> {sending ? "Sending…" : "Send broadcast"}</PrimaryBtn>
            </div>
        </Modal>
    );
};

export const RemindersCRM = () => {
    const [templates, setTemplates] = useState(null);
    const [config, setConfig] = useState(null);
    const [broadcasts, setBroadcasts] = useState([]);
    const [editTpl, setEditTpl] = useState(undefined);
    const [bcOpen, setBcOpen] = useState(false);
    const [singleOpen, setSingleOpen] = useState(false);

    const reload = async () => {
        const [t, c, b] = await Promise.all([getTemplates(), getCrmConfig(), getBroadcasts().catch(() => [])]);
        setTemplates(t); setConfig(c); setBroadcasts(b);
    };
    useEffect(() => {
        (async () => {
            try {
                const [t, c, b] = await Promise.all([getTemplates(), getCrmConfig(), getBroadcasts().catch(() => [])]);
                setTemplates(t); setConfig(c); setBroadcasts(b);
            } catch { toast.error("Could not load reminders."); }
        })();
    }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this message?")) return;
        try { await deleteTemplate(id); toast.success("Deleted."); reload(); }
        catch { toast.error("Could not delete."); }
    };

    const totalSent = broadcasts.reduce((a, b) => a + (b.sent || 0), 0);
    const totalRead = broadcasts.reduce((a, b) => a + ((b.stats || {}).read || 0), 0);
    const totalDelivered = broadcasts.reduce((a, b) => a + ((b.stats || {}).delivered || 0), 0);

    if (!templates || !config) return <div className="font-editorial italic text-oth-ink3" data-testid="reminders-crm-loading">Loading…</div>;

    return (
        <div data-testid="reminders-crm">
            <div className="grid grid-cols-3 gap-3 mb-6">
                <StatCard label="Messages sent" value={totalSent} testId="bc-stat-sent" />
                <StatCard label="Delivered" value={totalDelivered} accent="#0B3B60" testId="bc-stat-delivered" />
                <StatCard label="Read" value={totalRead} accent="#0B6B3A" testId="bc-stat-read" />
            </div>

            {/* Saved messages */}
            <div className="flex items-center justify-between mb-3">
                <div className="font-display text-base font-semibold tracking-tight">Saved messages</div>
                <div className="flex gap-2">
                    <GhostBtn onClick={() => setSingleOpen(true)} testId="open-single-send"><span className="inline-flex items-center gap-1.5"><User size={14} /> Send to a client</span></GhostBtn>
                    <PrimaryBtn onClick={() => setBcOpen(true)} testId="open-broadcast"><MessageSquare size={14} /> Broadcast</PrimaryBtn>
                    <GhostBtn onClick={() => setEditTpl(null)} testId="add-template"><span className="inline-flex items-center gap-1.5"><Plus size={14} /> New message</span></GhostBtn>
                </div>
            </div>

            {templates.length === 0 ? (
                <EmptyState title="No saved messages" body="Create reusable reminders (e.g. document requests, SIP setup guides) to send via WhatsApp or email." testId="templates-empty" />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
                    {templates.map((t) => (
                        <div key={t.id} className="rounded-2xl border border-oth-line/70 bg-white p-4" data-testid={`template-${t.id}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="font-display text-sm font-semibold">{t.name}</div>
                                    <div className="text-[10px] uppercase tracking-[0.16em] text-oth-ink3 mt-0.5">{t.channel}</div>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => setEditTpl(t)} className="w-8 h-8 rounded-lg flex items-center justify-center text-oth-ink2 hover:bg-[#EFEFEA]" data-testid={`template-edit-${t.id}`}><Pencil size={14} /></button>
                                    <button onClick={() => remove(t.id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-oth-ink3 hover:text-red-600" data-testid={`template-del-${t.id}`}><Trash2 size={14} /></button>
                                </div>
                            </div>
                            {t.body && <div className="mt-2 font-body text-sm text-oth-ink2 leading-relaxed line-clamp-3">{t.body}</div>}
                        </div>
                    ))}
                </div>
            )}

            {/* Broadcast history */}
            {broadcasts.length > 0 && (
                <>
                    <div className="font-display text-base font-semibold tracking-tight mb-3 flex items-center gap-2"><BarChart3 size={16} /> Broadcast history</div>
                    <div className="rounded-2xl border border-oth-line/70 bg-white overflow-x-auto">
                        <div className="min-w-[640px]">
                            <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-[#EFEFEA] text-[11px] uppercase tracking-[0.16em] text-oth-ink3 font-body">
                                <div className="col-span-4">Name</div><div className="col-span-2">Template</div>
                                <div className="col-span-1">Sent</div><div className="col-span-2">Delivered</div><div className="col-span-2">Read</div><div className="col-span-1">Failed</div>
                            </div>
                            {broadcasts.map((b) => (
                                <div key={b.id} className="grid grid-cols-12 gap-3 px-5 py-3 border-t border-oth-line/60 text-sm font-body items-center" data-testid={`broadcast-${b.id}`}>
                                    <div className="col-span-4 truncate">{b.name}<span className="block text-[11px] text-oth-ink3">{(b.created_at || "").slice(0, 10)}</span></div>
                                    <div className="col-span-2 truncate text-oth-ink3 text-[12px]">{b.template_name}</div>
                                    <div className="col-span-1">{b.sent ?? 0}/{b.total}</div>
                                    <div className="col-span-2">{(b.stats || {}).delivered || 0}</div>
                                    <div className="col-span-2 text-[#0B6B3A]">{(b.stats || {}).read || 0}</div>
                                    <div className="col-span-1 text-red-600">{(b.stats || {}).failed || 0}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {editTpl !== undefined && <TemplateModal key={editTpl?.id ?? "new"} onClose={() => setEditTpl(undefined)} tpl={editTpl} onSaved={reload} />}
            <BroadcastModal open={bcOpen} onClose={() => setBcOpen(false)} config={config} templates={templates} onSent={reload} />
            {singleOpen && <SingleSendModal onClose={() => setSingleOpen(false)} templates={templates} />}
        </div>
    );
};
